import { watch, computed, ref, reactive, readonly, toRefs } from 'vue'
import { api } from '@/Composables/Fulfillment/fulfillment-service-api'
import { settle } from '@/utils/promises'
import { LANG, CONTEXT } from '@/Composables/Fulfillment/constants'
import { router } from '@inertiajs/vue3'
import { useScanner, SCAN_PREFIX } from '@/Composables/scanner'
import { useSound, SOUND_TYPE } from '@/Composables/sound'
import { useErrors } from '@/Composables/errors'
import { FULFILLMENT_STATUS } from '@/Composables/Fulfillment/constants'

export const MODES = {
  // This mode is used when packing orders. (fulfillment page)
  PACKING: 'packing',
  // This mode is used when verifying orders. (deliveries page)
  VERIFYING: 'verifying'
}

export const useFulfillmentService = ({ order, mode = MODES.PACKING }) => {
  const itemCompletedKey = mode === MODES.PACKING ? 'quantity_fulfilled' : 'quantity_verified'
  const itemType = mode === MODES.PACKING ? 'packing' : 'verification'

  const tableData = ref()

  const { showError, showHardError } = useErrors()
  const { playSound } = useSound([SOUND_TYPE.COMPLETE])

  const createContext = () => {
    const state = reactive({
      current: CONTEXT.PACKING,
      prev: null
    })

    const set = (contextName) => {
      if (!CONTEXT[contextName]) {
        console.error(`Invalid context name: ${contextName}`)
        return
      }
      state.prev = state.current
      state.current = contextName
    }

    const revert = () => {
      state.current = state.prev
    }

    return {
      ...toRefs(readonly(state)),
      set,
      revert
    }
  }

  const context = createContext()

  const createItem = (item) => {
    return {
      ...item,
      type: itemType
    }
  }

  const syncOrder = async () => {
    const [err, result] = await settle(api.fetchOrder(order.id))

    if (err) {
      showHardError({ message: LANG.TABLE_DATA_FETCH_FAILED, confirmText: LANG.REFRESH })
      return
    }

    const [_, json] = result

    const items = (json.data?.items ?? []).map((item) => {
      return {
        ...item,
        _quantity_completed: item[itemCompletedKey] ?? 0
      }
    })

    tableData.value = sortTableData(items)
  }

  const activeFulfillmentRequests = ref(0)
  const fulfillItems = async (items) => {
    activeFulfillmentRequests.value++
    const resp = await settle(api.fulfillOrderItems(order.id, { items }))
    activeFulfillmentRequests.value--
    return resp
  }

  const completeOrder = async ({ notify = false } = {}) => {
    const [err, _] = await settle(api.completeOrder(order.id, notify))

    if (err) {
      console.error(err)
      showError({ message: LANG.ERROR_COMPLETING_ORDER })
      return
    }

    playSound(SOUND_TYPE.COMPLETE)
    router.visit(route('fulfillment.index'))
  }

  const verifyOrder = async () => {
    const [err, _] = await settle(api.verifyOrder(order.id))

    if (err) {
      console.error(err)
      showError({ message: LANG.ERROR_VERIFYING_ORDER })
      return
    }

    playSound(SOUND_TYPE.COMPLETE)
    router.visit(route('deliveries.index'))
  }

  const cancelOrder = async () => {
    const [err, _] = await settle(api.cancelOrders([order.id]))

    if (err) {
      console.error(err)
      showError({ message: LANG.ERROR_CANCELLING_ORDER })
      return
    }

    router.visit(route('fulfillment.index'))
  }

  const finalizeCancelOrder = async ({ notify = false } = {}) => {
    const [err, _] = await settle(api.finalizeCancelOrder(order.id, notify))

    if (err) {
      console.error(err)
      showError({ message: LANG.ERROR_FINALIZING_CANCELLATION })
      return
    }

    router.visit(route('fulfillment.index'))
  }

  const state = reactive({
    totalScanned: computed(() => {
      return (tableData?.value || []).reduce((total, item) => {
        return total + item._quantity_completed
      }, 0)
    }),
    totalToScan: computed(() => {
      return (tableData?.value || []).reduce((total, item) => {
        return total + item.quantity
      }, 0)
    }),
    canComplete: computed(() => {
      if (!tableData.value || activeFulfillmentRequests.value > 0) {
        return false
      }
      return state.totalScanned === state.totalToScan
    }),
    unrecognizedScanHasOccured: false
  })

  const shiftModifiedItem = (data, index) => {
    const modifiedItem = data[index]

    data.splice(index, 1)

    if (modifiedItem._quantity_completed === modifiedItem.quantity) {
      data.push(modifiedItem)
      return data
    }

    data.unshift(modifiedItem)
    return data
  }

  const sortTableData = (data) => {
    const newTableData = new Array(data.length)

    let remainingIndex = 0
    let completedIndex = data.length - 1

    for (let i = 0; i < data.length; i++) {
      const item = data[i]
      if (item._quantity_completed === item.quantity) {
        newTableData[completedIndex--] = item
      } else {
        newTableData[remainingIndex++] = item
      }
    }

    return newTableData
  }

  const findBySubLotNum = (identifier) => {
    return tableData.value.findIndex((item) => item.sub_lot_num === identifier)
  }

  const findByTagNum = (identifier) => {
    return tableData.value.findIndex(
      (item) => item.tag_num === identifier || item.tag_num.endsWith(identifier) || identifier.endsWith(item.tag_num)
    )
  }

  const updateItem = async ({ identifier, customQtyAdjustment, method = 'manual' } = {}) => {
    let matchType = 'sub_lot_num'
    let findFn = () => findBySubLotNum(identifier)
    let scannedItemIndex = findFn()

    if (scannedItemIndex === -1) {
      matchType = 'tag_num'
      findFn = () => findByTagNum(identifier)
      scannedItemIndex = findFn()
    }

    if (scannedItemIndex === -1) {
      state.unrecognizedScanHasOccured = true
      showError({ message: `${LANG.ITEM_NOT_RECOGNIZED} ${identifier}`, useToast: true })
      return
    }

    const scannedItem = tableData.value[scannedItemIndex]

    const qtyAdjustment = customQtyAdjustment ?? (scannedItem._quantity_completed > scannedItem.quantity ? -1 : 1)

    const newQty = scannedItem._quantity_completed + qtyAdjustment

    // If incrementing, ensure the new quantity is not greater than the allowed total quantity.
    if (qtyAdjustment > 0 && newQty > scannedItem.quantity) {
      showError({ message: LANG.ITEM_SCANNED_TOO_MANY, useToast: true })
      return
    } else if (qtyAdjustment < 0 && newQty < 0) {
      // If decrementing, ensure the new quantity is not less than 0.
      showError({ message: LANG.ERROR_TOO_MANY_ITEMS_REMOVED, useToast: true })
      return
    }

    // Eagerly update the table data to reflect the new quantity.
    tableData.value[scannedItemIndex] = { ...scannedItem, _quantity_completed: newQty }
    tableData.value = [...shiftModifiedItem(tableData.value, scannedItemIndex)]

    const [err, _] = await fulfillItems([
      createItem({
        sub_lot_num: matchType === 'sub_lot_num' ? scannedItem.sub_lot_num : scannedItem.tag_num,
        quantity: qtyAdjustment,
        method
      })
    ])

    if (err) {
      // Rollback the changes to the table data.
      // @Note: The sorting may have moved the item to a different index. Re-find.
      const movedScannedItemIndex = findFn()
      const currentState = tableData.value[movedScannedItemIndex]
      const rollbackQty = currentState._quantity_completed + qtyAdjustment * -1
      tableData.value[movedScannedItemIndex] = { ...scannedItem, _quantity_completed: rollbackQty }
      tableData.value = [...shiftModifiedItem(tableData.value, movedScannedItemIndex)]
      showError({ message: LANG.SCAN_FAILED_TO_SAVE, useToast: true })
      return
    }
  }

  const scanManager = {
    onDoubleScanDetected: async (identifier, prefix) => {
      if (prefix !== SCAN_PREFIX.ORDER || context.current.value !== CONTEXT.PACKING) {
        return
      }

      if (identifier !== String(order.id)) {
        showError({ message: LANG.ERROR_UNRECOGNIZED_ORDER, useToast: true })
        return
      }

      const action = (() => {
        if (mode === MODES.PACKING) {
          if (order.fulfillment_status === FULFILLMENT_STATUS.cancelled) {
            return { perform: finalizeCancelOrder, message: LANG.ERROR_ORDER_CANCELLATION_CANT_BE_FINALIZED }
          } else {
            return { perform: completeOrder, message: LANG.ERROR_ORDER_CANT_BE_COMPLETED }
          }
        }
        if (mode === MODES.VERIFYING) {
          return { perform: verifyOrder, message: LANG.ERROR_ORDER_CANT_BE_VERIFIED }
        }
      })()

      if (!state.canComplete) {
        showError({
          message: action.message,
          useToast: true
        })
        return
      }

      action.perform()
    },
    onScanDetected: (identifier, prefix) => {
      if (prefix) {
        return
      }

      if (context.current.value === CONTEXT.LOCKED) {
        return
      }

      if (context.current.value === CONTEXT.PACKING) {
        updateItem({ identifier, method: 'scan' })
        return
      }

      if (context.current.value === CONTEXT.SWAPPING) {
        swap.scannedSwapItem.value = identifier
      }
    }
  }

  const scanner = useScanner()

  scanner.addListener(scanManager.onScanDetected)
  scanner.addListener(scanManager.onDoubleScanDetected, { type: scanner.SCAN_TYPES.DOUBLE })

  watch(
    tableData,
    () => {
      scanner.buffer.flush()
    },
    {
      once: true
    }
  )

  const swap = {
    toSwap: ref(),
    scannedSwapItem: ref(),
    isSaving: ref(false),

    trigger: (swapData) => {
      swap.toSwap.value = swapData
      context.set(CONTEXT.SWAPPING)
    },

    clear: () => {
      swap.toSwap.value = undefined
      context.set(CONTEXT.PACKING)
    },

    clearScannedSwapItem: () => {
      swap.scannedSwapItem.value = undefined
    },

    saveSwapItems: async (items) => {
      swap.isSaving.value = true

      const [err, _] = await fulfillItems(items)

      swap.isSaving.value = false

      if (err) {
        showError({ message: LANG.ERROR_SWAPPING_ITEMS })
        return
      }

      syncOrder()
      swap.clear()
    }
  }

  return {
    order,
    tableData,

    context,
    state: readonly(state),

    swap,

    syncOrder,
    fulfillItems,
    completeOrder,
    cancelOrder,
    finalizeCancelOrder,
    verifyOrder,

    createItem,
    updateItem
  }
}
